/**
 * Lightweight Web Vitals Logger
 * Uses native PerformanceObserver API - no heavy libs
 * Captures LCP, CLS, INP, TTFB, FCP
 * Batches and sends to perf_metrics table via edge function
 */

import { supabase } from '@/integrations/supabase/client';

interface PerfMetric {
  page_path: string;
  metric_name: string;
  value: number;
  device_type: string;
  connection_type: string | null;
  user_agent: string;
  country_code: string | null;
}

// Detect device type from user agent
function getDeviceType(): string {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return 'mobile';
  return 'desktop';
}

// Get connection type from Network Information API
function getConnectionType(): string | null {
  const nav = navigator as any;
  if (nav.connection) {
    return nav.connection.effectiveType || null;
  }
  return null;
}

// Get cached country code
function getCountryCode(): string | null {
  try {
    const cache = localStorage.getItem('geo_check');
    if (cache) {
      const data = JSON.parse(cache);
      return data.country || null;
    }
  } catch (e) {
    // Ignore errors
  }
  return null;
}

// Queue for batching metrics
let metricsQueue: PerfMetric[] = [];
let flushTimeout: ReturnType<typeof setTimeout> | null = null;

// Flush metrics to Supabase
async function flushMetrics() {
  if (metricsQueue.length === 0) return;
  
  const batch = [...metricsQueue];
  metricsQueue = [];
  
  try {
    const { error } = await supabase.from('perf_metrics').insert(batch);
    if (error) {
      console.warn('[WebVitals] Failed to log metrics:', error.message);
    }
  } catch (e) {
    console.warn('[WebVitals] Error sending metrics:', e);
  }
}

// Queue a metric for batch sending
function queueMetric(name: string, value: number) {
  const metric: PerfMetric = {
    page_path: window.location.pathname,
    metric_name: name,
    value: Math.round(value * 100) / 100, // Round to 2 decimal places
    device_type: getDeviceType(),
    connection_type: getConnectionType(),
    user_agent: navigator.userAgent.substring(0, 256), // Limit length
    country_code: getCountryCode(),
  };
  
  metricsQueue.push(metric);
  
  // Debounce flush - send after 2 seconds of no new metrics
  if (flushTimeout) clearTimeout(flushTimeout);
  flushTimeout = setTimeout(flushMetrics, 2000);
}

// Initialize LCP Observer
function observeLCP() {
  if (!('PerformanceObserver' in window)) return;
  
  try {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      if (lastEntry) {
        queueMetric('LCP', lastEntry.startTime);
      }
    });
    observer.observe({ type: 'largest-contentful-paint', buffered: true });
  } catch (e) {
    // LCP not supported
  }
}

// Initialize CLS Observer
function observeCLS() {
  if (!('PerformanceObserver' in window)) return;
  
  let clsValue = 0;
  let clsEntries: PerformanceEntry[] = [];
  
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        // Only count layout shifts without user input
        if (!(entry as any).hadRecentInput) {
          clsEntries.push(entry);
          clsValue += (entry as any).value;
          
          // Log CLS sources for debugging (identifies shifting elements)
          const sources = (entry as any).sources;
          if (sources && sources.length > 0 && (entry as any).value > 0.01) {
            console.debug('[WebVitals] CLS source:', {
              value: (entry as any).value,
              elements: sources.map((s: any) => s.node?.nodeName || 'unknown'),
              selectors: sources.map((s: any) => {
                try { return s.node?.className?.substring(0, 80) || ''; } catch { return ''; }
              }),
            });
          }
        }
      }
    });
    observer.observe({ type: 'layout-shift', buffered: true });
    
    // Report final CLS on page hide
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && clsValue > 0) {
        queueMetric('CLS', clsValue);
        flushMetrics(); // Flush immediately on page hide
      }
    });
  } catch (e) {
    // CLS not supported
  }
}

// Initialize FCP Observer
function observeFCP() {
  if (!('PerformanceObserver' in window)) return;
  
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.name === 'first-contentful-paint') {
          queueMetric('FCP', entry.startTime);
          observer.disconnect();
        }
      }
    });
    observer.observe({ type: 'paint', buffered: true });
  } catch (e) {
    // FCP not supported
  }
}

// Initialize INP Observer - uses event-timing API per Web Vitals spec
// INP = max(duration) of all event-timing entries during session
function observeINP() {
  if (!('PerformanceObserver' in window)) return;
  
  // Store all interaction durations to compute p98 (per Web Vitals spec)
  const interactions: number[] = [];
  
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        // Cast to any for interactionId - not yet in TS types but valid in browsers
        const eventEntry = entry as any;
        // Only count discrete interactions (click, keypress, tap)
        // duration = total time from event dispatch to next paint
        if (eventEntry.duration > 0 && eventEntry.interactionId) {
          interactions.push(eventEntry.duration);
        }
      }
    });
    
    // Use 'event' type with durationThreshold for INP measurement
    observer.observe({ 
      type: 'event', 
      buffered: true,
      // @ts-ignore - durationThreshold is valid but not in TS types
      durationThreshold: 16 // Only events > 16ms (1 frame)
    });
    
    // Report INP on page hide - use p98 of all interactions (or max if <50)
    const reportINP = () => {
      if (interactions.length === 0) return;
      
      // Sort and get p98 (per Google's INP spec)
      interactions.sort((a, b) => b - a);
      const p98Index = Math.floor(interactions.length * 0.02);
      const inpValue = interactions[Math.min(p98Index, interactions.length - 1)];
      
      if (inpValue > 0) {
        queueMetric('INP', inpValue);
      }
    };
    
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        reportINP();
        flushMetrics(); // Ensure metrics are sent before page hides
      }
    });
    
    // Also report on pagehide for Safari
    window.addEventListener('pagehide', reportINP);
  } catch (e) {
    // INP not supported in this browser
    console.debug('[WebVitals] INP observer not supported');
  }
}

// Calculate TTFB from navigation timing
function measureTTFB() {
  try {
    const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navEntry) {
      const ttfb = navEntry.responseStart - navEntry.requestStart;
      if (ttfb > 0) {
        queueMetric('TTFB', ttfb);
      }
    }
  } catch (e) {
    // Navigation timing not available
  }
}

/**
 * Initialize Web Vitals monitoring
 * Call this once in your app's entry point
 */
export function initWebVitalsLogger() {
  // Check if geo-blocked
  try {
    const cache = localStorage.getItem('geo_check');
    if (cache) {
      const data = JSON.parse(cache);
      if (data.blocked) {
        console.log('[WebVitals] Skipping for blocked country');
        return;
      }
    }
  } catch (e) {
    // Continue if cache check fails
  }
  
  // Wait for page to be interactive
  if (document.readyState === 'complete') {
    startObservers();
  } else {
    window.addEventListener('load', startObservers);
  }
}

function startObservers() {
  // Use requestIdleCallback to avoid blocking main thread
  const start = () => {
    observeLCP();
    observeCLS();
    observeFCP();
    observeINP();
    measureTTFB();
  };
  
  if ('requestIdleCallback' in window) {
    requestIdleCallback(start, { timeout: 3000 });
  } else {
    setTimeout(start, 1000);
  }
}

/**
 * Manually report a custom metric
 */
export function reportCustomMetric(name: string, value: number) {
  queueMetric(name, value);
}
